import { useCallback, useEffect, useState } from "react";

/**
 * Служебный режим консоли: одна поверхность режется на две (docs/UI_PLAN.md §2).
 *
 * Хранится в `localStorage`, а не в состоянии сессии: популяции не пересекаются —
 * разработчик открывает панель каждый раз, копатель не откроет ни разу, и заново нажимать
 * на каждой перезагрузке пришлось бы только первому.
 *
 * НЕ привязан к `VERIFICATION_MODE`. Серверный флаг переключает поведение продукта, а это
 * клиентское предпочтение просмотра, и открывать панель нужно именно на продуктовой
 * поверхности — там, где живут баги.
 */
const STORAGE_KEY = "kopateli.devpanel";

const readStored = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "on";
  } catch {
    return false; // private mode / blocked storage: просто не запоминаем
  }
};

export const useDevMode = (): { devMode: boolean; toggleDev: () => void } => {
  const [devMode, setDevMode] = useState<boolean>(readStored);

  const set = useCallback((next: boolean) => {
    setDevMode(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? "on" : "off");
    } catch {
      // Не фатально: режим всё равно работает в этой сессии.
    }
  }, []);

  const toggleDev = useCallback(() => set(!devMode), [devMode, set]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      // `event.code`, и только он: на русской раскладке `event.key` для этой клавиши даёт «в».
      if (!event.ctrlKey || !event.altKey || event.code !== "KeyD") return;
      event.preventDefault();
      set(!devMode);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [devMode, set]);

  return { devMode, toggleDev };
};
